import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import DatabaseAccordion from "./database-accordion";
import UnusedObjectsHeader from "./unused-objects-header";
import UnusedObjectsSummary from "./unused-objects-summary";
import { useUnusedSqlObjects } from "./use-unused-sql-objects";

const UnusedSqlObjectsPage: React.FC = () => {
  const { data, isLoading, error, refresh } = useUnusedSqlObjects();
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedDatabase, setExpandedDatabase] = useState<string | false>(
    false,
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const totalFindings = useMemo(() => {
    return data?.results.reduce((sum, db) => sum + db.findingCount, 0) || 0;
  }, [data]);

  const filteredDatabases = useMemo(() => {
    if (!data?.results) return [];
    if (!searchQuery) return data.results;

    return data.results
      .map((db) => ({
        ...db,
        findings: db.findings.filter(
          (finding) =>
            finding.objectName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.schemaName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.objectType
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.reason.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
        findingCount: db.findings.filter(
          (finding) =>
            finding.objectName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.schemaName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.objectType
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            finding.reason.toLowerCase().includes(searchQuery.toLowerCase()),
        ).length,
      }))
      .filter((db) => db.findingCount > 0);
  }, [data, searchQuery]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load unused SQL objects data. Please try again later.
      </Alert>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 140px)",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "grey.200",
        background: "linear-gradient(to bottom, #ffffff, #fafafa)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <UnusedObjectsHeader
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        <UnusedObjectsSummary
          databaseCount={data?.results.length || 0}
          totalFindings={totalFindings}
        />
        <TextField
          fullWidth
          placeholder="Search by object name, schema, type, or reason..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Database Accordions */}
        {filteredDatabases.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {searchQuery
              ? "No objects found matching your search criteria."
              : "No unused SQL objects found."}
          </Alert>
        ) : (
          filteredDatabases.map((db) => (
            <DatabaseAccordion
              key={db.databaseName}
              database={db}
              expanded={expandedDatabase === db.databaseName}
              onToggle={(isExpanded) =>
                setExpandedDatabase(isExpanded ? db.databaseName : false)
              }
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default UnusedSqlObjectsPage;
